import React, { useMemo, useState } from 'react';
import {
  Container,
  Card,
  Row as BootstrapRow,
  Col as BootstrapCol,
  Button,
  Modal as BootstrapModal,
  Form as BootstrapForm,
} from 'react-bootstrap';
import { Table, Select, DatePicker, Form, Tag, App } from 'antd';
import { FilterOutlined, PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import FormItem from '@/common/FormItem/FormItem';
import LifecycleRulesDrawer, {
  LifecycleRulesInfoButton,
} from '@/common/LifecycleRulesDrawer/LifecycleRulesDrawer';
import { ALBUMS_LIFECYCLE_RULES } from '@/features/lifecycle-rules/lifecycleRulesContent';
import type { ColumnType, TableRowSelection } from 'antd/es/table/interface';
import PageTitle from '@/common/PageTitle/PageTitle';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { Link, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import styles from './albums.module.scss';

// Canonical Music Catalog — Albums page.
//
// This page is a VIEW over the canonical Album catalog
// (kk-admin-sa-music-catalog Slice #3), replacing the legacy
// listSongs('approved') group-by-album-string derivation. Albums are
// created automatically by the processing pipeline (resolve-or-create
// seam) or manually here; either way the canonical catalog is the only
// writer and this page never builds relationships client-side.
//
// Lifecycle (Song Library parity): Active → Archive → Restore | Permanent
// Delete. Albums use wire status `archived` (not `deleted`).
import type { Album, AlbumStatus } from '@/features/albums/albumsApi';
import {
  useListAlbumsQuery,
  useCreateAlbumMutation,
  useUpdateAlbumMutation,
  useArchiveAlbumMutation,
  useRestoreAlbumMutation,
  usePreflightPurgeAlbumMutation,
  usePurgeAlbumMutation,
  useBulkPurgeAlbumsMutation,
  extractAlbumErrorMessage,
  extractAlbumSongDependencyDetails,
} from '@/features/albums/albumsApi';
import { useListArtistsQuery } from '@/features/artists/artistsApi';
import { useListRecordLabelsQuery } from '@/features/record-labels/recordLabelsApi';
import { EntityRefBadge } from '@/components/EntityRef/EntityRefBadge';
import { formatEntityRef } from '@/lib/entityRef';
import {
  useCanEditPage,
  useCanPerformLifecycleDelete,
} from '@/contexts/SuperAdminIdentityContext';
import {
  CatalogLifecycleTabs,
  CatalogArchiveModal,
  CatalogRestoreModal,
  CatalogBatchConfirmModal,
  CatalogPermanentDeleteModal,
  CatalogPurgeFailuresModal,
  useCatalogPurgeFlow,
  useBatchActions,
  catalogBatchOptions,
  mixedSelectionWarning,
  type CatalogLifecycleView,
  type BatchActionValue,
  type SelectionLifecycle,
} from '@/features/catalog-lifecycle';

const PAGE_ID = 'albums';
const KIND = 'album' as const;

type AlbumRecord = {
  key: string;
  albumId: string;
  title: string;
  status: AlbumStatus;
  artistId: string;
  artistName: string;
  artistResolved: boolean;
  recordLabelId: string;
  recordLabelName: string;
  recordLabelResolved: boolean;
  releaseDateRaw: string;
  songCount: number;
  createdBySource: string;
  createdAt: string;
  updatedAt: string;
};

type AlbumEditorState = {
  title: string;
  primaryArtistId: string | undefined;
  recordLabelId: string | undefined;
  releaseDate: string;
};

const EMPTY_EDITOR: AlbumEditorState = {
  title: '',
  primaryArtistId: undefined,
  recordLabelId: undefined,
  releaseDate: '',
};

function isEditorValid(state: AlbumEditorState): boolean {
  return (
    state.title.trim().length > 0 &&
    Boolean(state.primaryArtistId) &&
    Boolean(state.recordLabelId)
  );
}

/** UI lifecycle view → albums API status (albums use real `archived`). */
function listStatusForView(view: CatalogLifecycleView): AlbumStatus | 'all' {
  return view === 'all' ? 'all' : view;
}

const Albums: React.FC = () => {
  const { message } = App.useApp();
  const canEdit = useCanEditPage(PAGE_ID);
  const canLifecycle = useCanPerformLifecycleDelete();

  const [view, setView] = useState<CatalogLifecycleView>('active');
  const [lifecycleHelpOpen, setLifecycleHelpOpen] = useState(false);

  const listStatus = listStatusForView(view);
  const {
    data: albumsData,
    isLoading,
    isFetching,
  } = useListAlbumsQuery({ status: listStatus, limit: 200 });
  // Tab counts — RTK Query dedupes when the main list shares the same args.
  const { data: activeCountData } = useListAlbumsQuery({
    status: 'active',
    limit: 200,
  });
  const { data: archivedCountData } = useListAlbumsQuery({
    status: 'archived',
    limit: 200,
  });

  // Name resolution from the same canonical catalog the other pages read —
  // song counts come from authoritative list `activeSongCount`.
  const { data: artistsData } = useListArtistsQuery({ status: 'active', limit: 200 });
  const { data: labelsData } = useListRecordLabelsQuery({ status: 'all', limit: 200 });

  const [createAlbum, createState] = useCreateAlbumMutation();
  const [updateAlbum, updateState] = useUpdateAlbumMutation();
  const [archiveAlbum, archiveState] = useArchiveAlbumMutation();
  const [restoreAlbum, restoreState] = useRestoreAlbumMutation();
  const [preflightPurgeAlbum] = usePreflightPurgeAlbumMutation();
  const [purgeAlbum] = usePurgeAlbumMutation();
  const [bulkPurgeAlbums] = useBulkPurgeAlbumsMutation();

  const {
    selectedRowKeys,
    setSelectedRowKeys,
    batchAction,
    batchModalOpen,
    chooseBatchAction,
    resetBatchUi,
    clearBatchAction,
  } = useBatchActions();

  const purgeFlow = useCatalogPurgeFlow({
    kind: KIND,
    canLifecycle,
    message,
    preflight: async (id) => preflightPurgeAlbum({ albumId: id }).unwrap(),
    purge: async ({ id, name, reason }) =>
      purgeAlbum({
        albumId: id,
        reason,
        confirmTitle: name,
        confirmAlbumId: id,
      }).unwrap(),
    bulkPurge: async ({ ids, reason }) =>
      bulkPurgeAlbums({ ids, reason }).unwrap(),
    extractErrorMessage: (err) => extractAlbumErrorMessage(err) || undefined,
    resetBatchUi,
  });

  const artistNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of artistsData?.artists ?? []) map.set(a.artistId, a.name);
    return map;
  }, [artistsData]);

  const labelNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of labelsData?.recordLabels ?? []) map.set(l.recordLabelId, l.name);
    return map;
  }, [labelsData]);

  const albums: AlbumRecord[] = useMemo(
    () =>
      (albumsData?.albums ?? []).map((album: Album) => ({
        key: album.albumId,
        albumId: album.albumId,
        title: album.title,
        status: album.status,
        artistId: album.primaryArtistId,
        // Unresolved names fall back to the human-facing AR-/RL- ref (never a
        // raw UUID) and render as muted technical values, not links.
        artistName:
          artistNames.get(album.primaryArtistId) ??
          formatEntityRef('artist', album.primaryArtistId),
        artistResolved: artistNames.has(album.primaryArtistId),
        recordLabelId: album.recordLabelId,
        recordLabelName:
          labelNames.get(album.recordLabelId) ??
          formatEntityRef('recordLabel', album.recordLabelId),
        recordLabelResolved: labelNames.has(album.recordLabelId),
        releaseDateRaw: album.releaseDate ?? '',
        songCount: album.activeSongCount ?? 0,
        createdBySource: album.createdBySource ?? '',
        createdAt: dayjs(album.createdAt).format('YYYY-MM-DD'),
        updatedAt: dayjs(album.updatedAt).format('YYYY-MM-DD'),
      })),
    [albumsData, artistNames, labelNames],
  );

  const activeCount = activeCountData?.albums?.length;
  const archivedCount = archivedCountData?.albums?.length;

  const artistOptions = useMemo(
    () =>
      (artistsData?.artists ?? [])
        .map((a) => ({ label: a.name, value: a.artistId }))
        .sort((x, y) => x.label.localeCompare(y.label)),
    [artistsData],
  );

  const labelOptions = useMemo(
    () =>
      (labelsData?.recordLabels ?? [])
        .filter((l) => l.status === 'active')
        .map((l) => ({ label: l.name, value: l.recordLabelId }))
        .sort((x, y) => x.label.localeCompare(y.label)),
    [labelsData],
  );

  // Filter state. `?title=` / `?artist=` deep-links from other catalog
  // pages (Song Library album cells) prefill the filters.
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    albumTitle: searchParams.get('title') ?? undefined,
    artistName: searchParams.get('artist') ?? undefined,
    createdAt: undefined as string | undefined,
    updatedAt: undefined as string | undefined,
  });
  const [resetCounter, setResetCounter] = useState(0);

  const filterOptions = useMemo(
    () => ({
      albumTitles: albums
        .map((a) => a.title)
        .sort((x, y) => x.localeCompare(y))
        .map((t) => ({ label: t, value: t })),
      artistNames: [...new Set(albums.map((a) => a.artistName))]
        .sort((x, y) => x.localeCompare(y))
        .map((n) => ({ label: n, value: n })),
    }),
    [albums],
  );

  const filteredAlbums = albums.filter((album) => {
    return (
      (!filters.albumTitle || album.title === filters.albumTitle) &&
      (!filters.artistName || album.artistName === filters.artistName) &&
      (!filters.createdAt || album.createdAt === filters.createdAt) &&
      (!filters.updatedAt || album.updatedAt === filters.updatedAt)
    );
  });

  const selectedRows = useMemo(
    () => filteredAlbums.filter((a) => selectedRowKeys.includes(a.key)),
    [filteredAlbums, selectedRowKeys],
  );

  const selectionLifecycle: SelectionLifecycle = useMemo(() => {
    if (selectedRows.length === 0) return 'none';
    const statuses = new Set(selectedRows.map((r) => r.status));
    if (statuses.size > 1) return 'mixed';
    return selectedRows[0].status === 'archived' ? 'archived' : 'active';
  }, [selectedRows]);

  // Status-scoped subsets of the current selection — mixed All-tab
  // selections run each batch action against its matching subset only
  // (owner-approved 2026-08-11, Song Library parity).
  const selectedActiveIds = useMemo(
    () =>
      selectedRows.filter((r) => r.status !== 'archived').map((r) => r.key),
    [selectedRows],
  );
  const selectedArchivedIds = useMemo(
    () =>
      selectedRows.filter((r) => r.status === 'archived').map((r) => r.key),
    [selectedRows],
  );

  const batchOptions = useMemo(
    () =>
      catalogBatchOptions(view, selectionLifecycle, {
        active: selectedActiveIds.length,
        archived: selectedArchivedIds.length,
      }),
    [view, selectionLifecycle, selectedActiveIds.length, selectedArchivedIds.length],
  );

  /** Ids the latched batch action will run against (scoped when mixed). */
  const batchTargetIds = useMemo(() => {
    if (selectionLifecycle === 'mixed') {
      if (batchAction === 'archive') return selectedActiveIds;
      if (batchAction === 'restore') return selectedArchivedIds;
      return [];
    }
    return selectedRowKeys.map(String);
  }, [selectionLifecycle, batchAction, selectedActiveIds, selectedArchivedIds, selectedRowKeys]);

  // Selection framework stays visible on every lifecycle tab when allowed.
  const rowSelection: TableRowSelection<AlbumRecord> | undefined = canLifecycle
    ? {
        selectedRowKeys,
        onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
      }
    : undefined;

  // Add / Edit / Archive / Restore modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addEditor, setAddEditor] = useState<AlbumEditorState>(EMPTY_EDITOR);
  const [editingAlbum, setEditingAlbum] = useState<AlbumRecord | null>(null);
  const [editEditor, setEditEditor] = useState<AlbumEditorState>(EMPTY_EDITOR);
  const editModalOpen = editingAlbum != null;
  const [archivingAlbum, setArchivingAlbum] = useState<AlbumRecord | null>(null);
  const [restoringAlbum, setRestoringAlbum] = useState<AlbumRecord | null>(null);

  const switchView = (next: CatalogLifecycleView) => {
    setView(next);
    resetBatchUi();
  };

  const handleBatchAction = (value: BatchActionValue) => {
    if (!canLifecycle) return;
    chooseBatchAction(value, selectionLifecycle, {
      onMixedSelection: () => {
        message.warning(mixedSelectionWarning('albums'));
      },
      onDelete: (ids) => void purgeFlow.openBulk(ids),
      mixedScope: {
        activeIds: selectedActiveIds,
        archivedIds: selectedArchivedIds,
      },
    });
  };

  const handleConfirmBatch = async () => {
    if (!canLifecycle || !batchAction) return;
    if (batchAction !== 'archive' && batchAction !== 'restore') return;
    if (batchTargetIds.length === 0) return;
    let failures = 0;
    for (const key of batchTargetIds) {
      try {
        // eslint-disable-next-line no-await-in-loop
        if (batchAction === 'archive') {
          await archiveAlbum({ albumId: String(key) }).unwrap();
        } else {
          await restoreAlbum({ albumId: String(key) }).unwrap();
        }
      } catch {
        failures += 1;
      }
    }
    const succeeded = batchTargetIds.length - failures;
    if (batchAction === 'archive') {
      if (succeeded > 0) message.success(`Archived ${succeeded} album${succeeded === 1 ? '' : 's'}`);
      if (failures > 0) message.error(`${failures} archive${failures === 1 ? '' : 's'} failed`);
    } else {
      if (succeeded > 0) message.success(`Restored ${succeeded} album${succeeded === 1 ? '' : 's'}`);
      if (failures > 0) message.error(`${failures} restore${failures === 1 ? '' : 's'} failed`);
    }
    resetBatchUi();
  };

  // Filter change
  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({
      albumTitle: undefined,
      artistName: undefined,
      createdAt: undefined,
      updatedAt: undefined,
    });
    setView('active');
    resetBatchUi();
    setResetCounter((prev) => prev + 1);
  };

  // Add album
  const openAdd = () => {
    if (!canEdit) return;
    setAddEditor(EMPTY_EDITOR);
    setAddModalOpen(true);
  };

  const handleCancelAdd = () => {
    setAddEditor(EMPTY_EDITOR);
    setAddModalOpen(false);
  };

  const submitAdd = async () => {
    if (!isEditorValid(addEditor)) return;
    const payload: {
      title: string;
      primaryArtistId: string;
      recordLabelId: string;
      releaseDate?: string;
    } = {
      title: addEditor.title.trim(),
      primaryArtistId: addEditor.primaryArtistId as string,
      recordLabelId: addEditor.recordLabelId as string,
    };
    if (addEditor.releaseDate) payload.releaseDate = addEditor.releaseDate;
    try {
      const created = await createAlbum(payload).unwrap();
      message.success(`Created album "${created.title}"`);
      setAddEditor(EMPTY_EDITOR);
      setAddModalOpen(false);
    } catch (err: unknown) {
      const msg = extractAlbumErrorMessage(err);
      if (msg) message.error(msg);
    }
  };

  // Edit album
  const openEdit = (record: AlbumRecord) => {
    if (!canEdit || record.status !== 'active') return;
    setEditingAlbum(record);
    setEditEditor({
      title: record.title,
      primaryArtistId: record.artistId,
      recordLabelId: record.recordLabelId,
      releaseDate: record.releaseDateRaw,
    });
  };

  const handleCancelEdit = () => {
    setEditEditor(EMPTY_EDITOR);
    setEditingAlbum(null);
  };

  const submitEdit = async () => {
    if (!editingAlbum) return;
    if (!isEditorValid(editEditor)) return;
    const patch: {
      title?: string;
      primaryArtistId?: string;
      recordLabelId?: string;
      releaseDate?: string | null;
    } = {};
    const trimmedTitle = editEditor.title.trim();
    if (trimmedTitle !== editingAlbum.title) patch.title = trimmedTitle;
    if (editEditor.primaryArtistId !== editingAlbum.artistId) {
      patch.primaryArtistId = editEditor.primaryArtistId;
    }
    if (editEditor.recordLabelId !== editingAlbum.recordLabelId) {
      patch.recordLabelId = editEditor.recordLabelId;
    }
    if (editEditor.releaseDate !== editingAlbum.releaseDateRaw) {
      patch.releaseDate = editEditor.releaseDate === '' ? null : editEditor.releaseDate;
    }
    if (Object.keys(patch).length === 0) {
      setEditEditor(EMPTY_EDITOR);
      setEditingAlbum(null);
      return;
    }
    try {
      const updated = await updateAlbum({
        albumId: editingAlbum.albumId,
        ...patch,
      }).unwrap();
      message.success(`Updated album "${updated.title}"`);
      setEditEditor(EMPTY_EDITOR);
      setEditingAlbum(null);
    } catch (err: unknown) {
      const msg = extractAlbumErrorMessage(err);
      if (msg) message.error(msg);
    }
  };

  const submitArchive = async () => {
    if (!archivingAlbum) return;
    try {
      await archiveAlbum({ albumId: archivingAlbum.albumId }).unwrap();
      message.success(`Archived album "${archivingAlbum.title}"`);
      setArchivingAlbum(null);
    } catch (err: unknown) {
      const msg = extractAlbumErrorMessage(err);
      const dep = extractAlbumSongDependencyDetails(err);
      if (dep?.songLibraryPath) {
        message.error(
          `${msg || 'Archive blocked'} — open Song Library to reassign or permanently delete referencing songs.`,
        );
      } else if (msg) {
        message.error(msg);
      }
    }
  };

  const submitRestore = async () => {
    if (!restoringAlbum) return;
    try {
      await restoreAlbum({ albumId: restoringAlbum.albumId }).unwrap();
      message.success(`Restored album "${restoringAlbum.title}"`);
      setRestoringAlbum(null);
    } catch (err: unknown) {
      const msg = extractAlbumErrorMessage(err);
      if (msg) message.error(msg);
    }
  };

  const columns: ColumnType<AlbumRecord>[] = [
    {
      title: 'Album Name',
      dataIndex: 'title',
      sorter: (a: AlbumRecord, b: AlbumRecord) => a.title.localeCompare(b.title),
      width: 220,
      render: (_: unknown, record: AlbumRecord) => (
        <span>
          {record.title}
          {record.createdBySource === 'pipeline' && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              Auto-created
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: 'Artist',
      dataIndex: 'artistName',
      sorter: (a: AlbumRecord, b: AlbumRecord) => a.artistName.localeCompare(b.artistName),
      width: 180,
      render: (_: unknown, record: AlbumRecord) =>
        record.artistResolved ? (
          <Link to={`/artists?name=${encodeURIComponent(record.artistName)}`}>
            {record.artistName}
          </Link>
        ) : (
          <EntityRefBadge kind="artist" id={record.artistId} muted />
        ),
    },
    {
      title: 'Record Label',
      dataIndex: 'recordLabelName',
      sorter: (a: AlbumRecord, b: AlbumRecord) =>
        a.recordLabelName.localeCompare(b.recordLabelName),
      width: 160,
      render: (_: unknown, record: AlbumRecord) =>
        record.recordLabelResolved ? (
          record.recordLabelName
        ) : (
          <EntityRefBadge kind="recordLabel" id={record.recordLabelId} muted />
        ),
    },
    {
      title: 'Songs',
      dataIndex: 'songCount',
      sorter: (a: AlbumRecord, b: AlbumRecord) => a.songCount - b.songCount,
      width: 80,
      render: (count: number, record: AlbumRecord) => (
        <Link to={`/songs?albumId=${encodeURIComponent(record.albumId)}&status=active`}>
          {count}
        </Link>
      ),
    },
    {
      title: 'Release Date',
      dataIndex: 'releaseDateRaw',
      sorter: (a: AlbumRecord, b: AlbumRecord) =>
        a.releaseDateRaw.localeCompare(b.releaseDateRaw),
      width: 130,
      render: (value: string) => value || '—',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      sorter: (a: AlbumRecord, b: AlbumRecord) =>
        dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      width: 130,
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      sorter: (a: AlbumRecord, b: AlbumRecord) =>
        dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix(),
      width: 130,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_: unknown, record: AlbumRecord) => {
        const isActive = record.status === 'active';
        const isArchived = record.status === 'archived';
        return (
          <span>
            {canEdit && isActive ? (
              <a
                style={{ marginRight: 12, cursor: 'pointer', color: '#1677ff' }}
                onClick={() => openEdit(record)}
              >
                Edit
              </a>
            ) : null}
            {canLifecycle && isActive ? (
              <a
                style={{ color: '#e21e26', cursor: 'pointer' }}
                onClick={() => setArchivingAlbum(record)}
              >
                Archive
              </a>
            ) : null}
            {canLifecycle && isArchived ? (
              <>
                <a
                  style={{ marginRight: 12, color: '#1677ff', cursor: 'pointer' }}
                  onClick={() => setRestoringAlbum(record)}
                >
                  Restore
                </a>
                <a
                  style={{ color: '#e21e26', cursor: 'pointer' }}
                  onClick={() => void purgeFlow.openSingle(record.albumId, record.title)}
                >
                  Purge
                </a>
              </>
            ) : null}
          </span>
        );
      },
    },
  ];

  const batchConfirmAction =
    batchAction === 'archive' || batchAction === 'restore' ? batchAction : null;

  return (
    <Container fluid>
      <Card style={{ borderRadius: 12 }}>
        <Card.Body>
          <Card.Title
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <PageTitle title="Albums" />
              <LifecycleRulesInfoButton onClick={() => setLifecycleHelpOpen(true)} />
            </span>
            <Button
              onClick={handleReset}
              style={{
                background: 'white',
                color: '#333',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500',
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>

          <CatalogLifecycleTabs
            view={view}
            onChange={switchView}
            activeCount={activeCount}
            archivedCount={archivedCount}
            testId="albums-lifecycle-tabs"
          />

          <Form layout="vertical" className={styles.form}>
            <BootstrapRow>
              <BootstrapCol>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Album Name"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`albumTitle-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any album name"
                            value={filters.albumTitle}
                            onChange={(value) => handleFilterChange('albumTitle', value)}
                            options={filterOptions.albumTitles}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Artist"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`artistName-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any artist"
                            value={filters.artistName}
                            onChange={(value) => handleFilterChange('artistName', value)}
                            options={filterOptions.artistNames}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Created at"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <DatePicker
                            key={`createdAt-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.dateField}`}
                            placeholder="Created at"
                            value={filters.createdAt ? dayjs(filters.createdAt) : undefined}
                            onChange={(date) =>
                              handleFilterChange(
                                'createdAt',
                                date ? date.format('YYYY-MM-DD') : undefined,
                              )
                            }
                          />
                        </FormItem>
                      </BootstrapCol>
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Updated at"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <DatePicker
                            key={`updatedAt-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.dateField}`}
                            placeholder="Updated at"
                            value={filters.updatedAt ? dayjs(filters.updatedAt) : undefined}
                            onChange={(date) =>
                              handleFilterChange(
                                'updatedAt',
                                date ? date.format('YYYY-MM-DD') : undefined,
                              )
                            }
                          />
                        </FormItem>
                      </BootstrapCol>
                      {canLifecycle ? (
                        <BootstrapCol md={4}>
                          <FormItem
                            label="Batch Actions"
                            className="customSelectWrapper"
                            layout="vertical"
                          >
                            <Select<BatchActionValue>
                              className={`customSelect ${styles.selectField}`}
                              placeholder="Batch Actions"
                              value={batchAction}
                              onChange={handleBatchAction}
                              disabled={
                                selectedRowKeys.length === 0 ||
                                batchOptions.length === 0
                              }
                              options={batchOptions}
                            />
                          </FormItem>
                        </BootstrapCol>
                      ) : (
                        <BootstrapCol md={4} />
                      )}
                      <BootstrapCol md={4} className="d-flex align-items-center mt-2 mt-md-0">
                        {canEdit ? (
                          <Button
                            className="w-100"
                            style={{
                              background: 'white',
                              color: '#e21e26',
                              border: '1px solid #e21e26',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              justifyContent: 'center',
                              height: '50px',
                              marginTop: '8px',
                            }}
                            onClick={openAdd}
                          >
                            <PlusOutlined style={{ fontSize: 16 }} />
                            Add New Album
                          </Button>
                        ) : null}
                      </BootstrapCol>
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
              </BootstrapCol>
            </BootstrapRow>
          </Form>

          <Table
            className={`legacy-row-height-table ${styles.table}`}
            rowSelection={rowSelection}
            columns={columns}
            dataSource={filteredAlbums}
            pagination={{ pageSize: 10, showQuickJumper: true }}
            rowKey="key"
            bordered
            scroll={{ x: true }}
            loading={{
              spinning: isLoading || isFetching,
              indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />,
            }}
          />
        </Card.Body>
      </Card>

      {/* Add New Album Modal — react-bootstrap to match Artists/Channel Admin styling */}
      <BootstrapModal show={addModalOpen} onHide={handleCancelAdd} size="lg">
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>Add New Album</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <BootstrapForm>
            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Album Title *</BootstrapForm.Label>
              <BootstrapForm.Control
                type="text"
                placeholder="Enter album title"
                value={addEditor.title}
                maxLength={512}
                onChange={(e) =>
                  setAddEditor((prev) => ({ ...prev, title: e.target.value }))
                }
              />
            </BootstrapForm.Group>

            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Primary Artist *</BootstrapForm.Label>
              <Select
                showSearch
                optionFilterProp="label"
                getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
                placeholder="Select artist"
                style={{ width: '100%' }}
                value={addEditor.primaryArtistId}
                onChange={(value) =>
                  setAddEditor((prev) => ({ ...prev, primaryArtistId: value }))
                }
                options={artistOptions}
              />
              <BootstrapForm.Text className="text-muted">
                Album uniqueness is Title + Primary Artist — the same title can
                exist for different artists.
              </BootstrapForm.Text>
            </BootstrapForm.Group>

            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Record Label *</BootstrapForm.Label>
              <Select
                showSearch
                optionFilterProp="label"
                getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
                placeholder="Select record label"
                style={{ width: '100%' }}
                value={addEditor.recordLabelId}
                onChange={(value) =>
                  setAddEditor((prev) => ({ ...prev, recordLabelId: value }))
                }
                options={labelOptions}
              />
            </BootstrapForm.Group>

            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Release Date</BootstrapForm.Label>
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Optional"
                value={addEditor.releaseDate ? dayjs(addEditor.releaseDate) : undefined}
                onChange={(date) =>
                  setAddEditor((prev) => ({
                    ...prev,
                    releaseDate: date ? date.format('YYYY-MM-DD') : '',
                  }))
                }
              />
            </BootstrapForm.Group>
          </BootstrapForm>
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button variant="secondary" onClick={handleCancelAdd}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submitAdd}
            disabled={!isEditorValid(addEditor) || createState.isLoading}
            style={{ background: '#e21e26', border: 'none' }}
          >
            {createState.isLoading ? 'Creating…' : 'Create Album'}
          </Button>
        </BootstrapModal.Footer>
      </BootstrapModal>

      {/* Edit Album Modal */}
      <BootstrapModal show={editModalOpen} onHide={handleCancelEdit} size="lg">
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>Edit Album</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <BootstrapForm>
            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Album Title *</BootstrapForm.Label>
              <BootstrapForm.Control
                type="text"
                placeholder="Enter album title"
                value={editEditor.title}
                maxLength={512}
                onChange={(e) =>
                  setEditEditor((prev) => ({ ...prev, title: e.target.value }))
                }
              />
            </BootstrapForm.Group>

            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Primary Artist *</BootstrapForm.Label>
              <Select
                showSearch
                optionFilterProp="label"
                getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
                placeholder="Select artist"
                style={{ width: '100%' }}
                value={editEditor.primaryArtistId}
                onChange={(value) =>
                  setEditEditor((prev) => ({ ...prev, primaryArtistId: value }))
                }
                options={artistOptions}
              />
            </BootstrapForm.Group>

            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Record Label *</BootstrapForm.Label>
              <Select
                showSearch
                optionFilterProp="label"
                getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
                placeholder="Select record label"
                style={{ width: '100%' }}
                value={editEditor.recordLabelId}
                onChange={(value) =>
                  setEditEditor((prev) => ({ ...prev, recordLabelId: value }))
                }
                options={labelOptions}
              />
            </BootstrapForm.Group>

            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Release Date</BootstrapForm.Label>
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Optional — clearing removes the release date"
                value={editEditor.releaseDate ? dayjs(editEditor.releaseDate) : undefined}
                onChange={(date) =>
                  setEditEditor((prev) => ({
                    ...prev,
                    releaseDate: date ? date.format('YYYY-MM-DD') : '',
                  }))
                }
              />
            </BootstrapForm.Group>
          </BootstrapForm>
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button variant="secondary" onClick={handleCancelEdit}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submitEdit}
            disabled={!isEditorValid(editEditor) || updateState.isLoading}
            style={{ background: '#e21e26', border: 'none' }}
          >
            {updateState.isLoading ? 'Saving…' : 'Save Changes'}
          </Button>
        </BootstrapModal.Footer>
      </BootstrapModal>

      <CatalogArchiveModal
        kind={KIND}
        open={archivingAlbum != null}
        name={archivingAlbum?.title}
        loading={archiveState.isLoading}
        onCancel={() => setArchivingAlbum(null)}
        onConfirm={() => void submitArchive()}
      />

      <CatalogRestoreModal
        kind={KIND}
        open={restoringAlbum != null}
        name={restoringAlbum?.title}
        loading={restoreState.isLoading}
        onCancel={() => setRestoringAlbum(null)}
        onConfirm={() => void submitRestore()}
      />

      <CatalogBatchConfirmModal
        kind={KIND}
        open={batchModalOpen}
        action={batchConfirmAction}
        selectedCount={batchTargetIds.length}
        mixedScoped={selectionLifecycle === 'mixed'}
        loading={archiveState.isLoading || restoreState.isLoading}
        onCancel={clearBatchAction}
        onConfirm={() => void handleConfirmBatch()}
      />

      <CatalogPermanentDeleteModal {...purgeFlow.modalProps} />
      <CatalogPurgeFailuresModal {...purgeFlow.failuresProps} />

      <LifecycleRulesDrawer
        content={ALBUMS_LIFECYCLE_RULES}
        open={lifecycleHelpOpen}
        onClose={() => setLifecycleHelpOpen(false)}
      />
    </Container>
  );
};

export default withAuthenticationRequired(Albums);
